import { Text, TextColor, TextVariant } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import type { StepCardProps } from './types';

const SHADOW_DEFAULT = 'shadow-[0px_4px_6px_-1px_rgba(10,13,18,0.1),0px_2px_4px_-2px_rgba(10,13,18,0.06)]';
const SHADOW_HOVER =
  'hover:shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03),0px_2px_2px_-1px_rgba(10,13,18,0.04)]';

function StepBadge({ stepNumber, isDisabled }: { stepNumber: number; isDisabled: boolean }) {
  return (
    <div
      className={cx(
        'flex shrink-0 items-center justify-center',
        'h-[36px] w-[36px] rounded-[2px]',
        'bg-indigo-50 px-[10px] py-[2px]',
      )}
    >
      <Text
        variant={TextVariant.SmMedium}
        color={TextColor.Inherit}
        showEllipsis={false}
        elementType="span"
        className={cx('text-center', isDisabled ? 'text-indigo-200' : 'text-indigo-700')}
      >
        {stepNumber}
      </Text>
    </div>
  );
}

function StepCard({
  stepNumber,
  title,
  subtitle,
  isActive = true,
  isDisabled = false,
  onHoverChange,
  onPress,
}: StepCardProps) {
  const handleHoverChange = (hovered: boolean) => {
    if (!isDisabled) onHoverChange?.(hovered);
  };

  const handleClick = () => {
    if (!isDisabled) onPress?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPress?.();
      onHoverChange?.(true);
    } else if (e.key === 'Escape') {
      onHoverChange?.(false);
    }
  };

  return (
    <div
      data-active={isActive}
      data-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? undefined : 0}
      onMouseEnter={() => handleHoverChange(true)}
      onMouseLeave={() => handleHoverChange(false)}
      onFocus={() => handleHoverChange(true)}
      onBlur={() => handleHoverChange(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cx(
        !isDisabled && onPress && 'cursor-pointer',
        'group flex flex-1 items-center gap-[16px]',
        'h-[52px] min-w-0 overflow-clip',
        'px-[8px] py-[6px]',
        'transition-all duration-150',
        !isActive && 'opacity-80',
        isDisabled ? '' : cx('bg-white border border-secondary', SHADOW_DEFAULT, SHADOW_HOVER, 'hover:border-primary'),
      )}
    >
      <StepBadge stepNumber={stepNumber} isDisabled={isDisabled} />
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <Text
          variant={TextVariant.SmMedium}
          elementType="p"
          color={isDisabled ? TextColor.Inherit : TextColor.Secondary}
          className={cx(!isDisabled && 'group-hover:text-secondary-hover', isDisabled && 'text-placeholder-subtle')}
        >
          {title}
        </Text>
        <Text
          variant={TextVariant.XsMedium}
          elementType="p"
          color={isDisabled ? TextColor.Inherit : TextColor.Quaternary}
          className={cx(isDisabled && 'text-placeholder-subtle')}
        >
          {subtitle}
        </Text>
      </div>
    </div>
  );
}

export { StepCard };
